import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const apiBaseUrl = (baseUrl: string | undefined) =>
  (baseUrl?.trim() || "https://api.endorlabs.com/v1").replace(/\/+$/, "");

export const endorLabs = defineConnector({
  identifier: "endorLabs",
  name: "Endor Labs",
  tagLine: "Software supply chain security",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: ({ baseUrl }) =>
    exactUrlToRegex(baseUrl || "https://api.endorlabs.com/v1"),
  auth: ({ configuration }) =>
    fetchTokenAuth({
      request: {
        url: {
          from: "literal",
          value: apiBaseUrl(configuration.baseUrl) + "/auth/api-key",
        },
        headers: [
          {
            name: "Content-Type",
            value: { from: "literal", value: "application/json" },
          },
        ],
        jsonBody: {
          key: { from: "secret", field: "apiKey" },
          secret: { from: "secret", field: "apiSecret" },
        },
      },
      extract: { from: "jsonPath", paths: ["token"] },
      apply: {
        kind: "header",
        name: "Authorization",
        scheme: "Bearer",
        extraHeaders: [],
      },
    }),
  steps: [
    {
      title: "Set up Endor Labs",
      items: [
        {
          kind: "markdown",
          content:
            "In Endor Labs, open **User menu → Settings → Access Control → API Keys** and generate an API key with **Read-only** access. Copy the key and secret, and enter the namespace you want to access. See [API authentication](https://docs.endorlabs.com/developers-api/rest-api/authentication).",
        },
      ],
    },
    {
      title: "Enter connection details",
      items: [
        {
          kind: "baseUrl",
          label: "API base URL",
          required: true,
          defaultValue: "https://api.endorlabs.com/v1",
          target: {
            kind: "configuration",
            field: "baseUrl",
          },
          helperText:
            "Use https://api.endorlabs.com/v1, or the API base URL provided for your Endor Labs deployment. Enter the namespace separately below.",
        },
        {
          kind: "input",
          label: "API key",
          required: true,
          target: {
            kind: "secrets",
            field: "apiKey",
          },
          helperText:
            "The Endor Labs API key generated under Settings → Access Control → API Keys.",
        },
        {
          kind: "input",
          label: "API secret",
          required: true,
          target: {
            kind: "secrets",
            field: "apiSecret",
          },
          helperText:
            "The API secret returned when the Endor Labs API key is generated. Store it securely because Endor Labs states it is not accessible through the UI afterward.",
        },
        {
          kind: "input",
          label: "Namespace",
          required: true,
          target: {
            kind: "configuration",
            field: "namespace",
          },
          helperText: "Enter the Endor Labs namespace you want to access.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl, namespace }) => ({
      url:
        apiBaseUrl(baseUrl) +
        "/namespaces/" +
        encodeURIComponent(namespace) +
        "/projects?list_parameters.page_size=1",
      ...{
        method: "GET",
        headers: { "Content-Type": "application/jsoncompact" },
      },
    }),
  },
});
